import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AppState,
    AuthService,
    User,
    AuthClientConfig,
    AuthConfig,
} from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import {
    User as ClientUser,
    AccountTelephone,
    ErrorResponse,
    GetCurrentUser200Response,
    UsersService,
} from '@viewbid/ts-angular-client';
import {
    UserStateChangeAction,
    TokenStorageService,
    JWTTokens,
    TokenType,
    User as StoreUser,
} from 'viewbid-shared-lib';
import {
    defer,
    switchMap,
    catchError,
    finalize,
    map,
    tap,
    of,
    shareReplay,
    EMPTY,
    Observable,
} from 'rxjs';
import { UsersErrorCodes } from '../../modules/profiles/enums/users-error-codes';

export enum ProfileStatus {
    Complete = 'complete',
    Incomplete = 'incomplete',
    Unknown = 'unknown',
}

function isCompleteStoredUser(storedUser: Partial<StoreUser> | null): boolean {
    return !!storedUser?.accountMappingsId && Array.isArray(storedUser.roles);
}

@Injectable({
    providedIn: 'root',
})
export class ProfileService {
    private appSessionExchange$: Observable<JWTTokens> | null = null;

    constructor(
        private auth0Config: AuthClientConfig,
        private authService: AuthService,
        private store: Store<AppState>,
        private userService: UsersService,
        private tokenStorageService: TokenStorageService,
    ) {}

    /**
     * Force a refresh of the user profile using the current token
     * This is particularly useful during masquerade when we need to
     * get the masqueraded user's profile
     */
    public forceProfileRefresh(): void {
        console.log(
            'Force profile refresh triggered. Fetching current user details.',
        );
        this.userService.getCurrentUser().subscribe({
            next: (response: any) => {
                console.log('Force profile refresh response:', response);
                if (response.data) {
                    const telephone: AccountTelephone | undefined =
                        response.data.telephone;
                    this.store.dispatch(
                        new UserStateChangeAction({
                            data: {
                                username: response.data.user?.username ?? '',
                                email: response.data.user?.email?.email ?? '',
                                isEmailVerified: true,
                                pictureUrl: '',
                                ssoId: '',
                                firstname:
                                    response.data.user?.firstname ?? null,
                                lastname: response.data.user?.lastname ?? null,
                                accountMappingsId:
                                    response.data.user?.accountMappingsId ??
                                    null,
                                accountTelephone: telephone ?? null,
                                createdOn:
                                    response.data.user?.createdOn ?? null,
                                registrationStatus:
                                    response.data.user?.status ?? null,
                                spendingCap:
                                    response.data.accountPreference
                                        ?.spendingCap ?? null,
                                isTelephoneVerified: telephone?.isVerified,
                                roles:
                                    response.data.accountPreference?.preferences
                                        ?.roles ?? [],
                            },
                        }),
                    );
                }
            },
            error: (err: any) => {
                console.error('Error fetching user profile:', err);
                this.store.dispatch(new UserStateChangeAction({ data: null }));
            },
        });
    }

    private getCurrentUserConfig(): any {
        const config: AuthConfig = this.auth0Config.get();
        // eslint-disable-next-line
        const auth0Token = this.tokenStorageService.getLocalStorageItem(
            `@@auth0spajs@@::${config.clientId}::@@user@@`,
        );

        return auth0Token?.decodedToken?.user;
    }

    public resolveProfileStatus(): Observable<ProfileStatus> {
        return this.authService.getAccessTokenSilently().pipe(
            switchMap(() =>
                this.tokenStorageService.getToken()
                    ? this.currentUserProfileStatus()
                    : this.ssoLoginProfileStatus(),
            ),
            catchError(() => of(ProfileStatus.Unknown)),
        );
    }

    private ssoLoginProfileStatus(): Observable<ProfileStatus> {
        return this.exchangeAppSession().pipe(
            switchMap(() => this.currentUserProfileStatus()),
            catchError((error: unknown) =>
                of(
                    this.isAccountNotFound(error)
                        ? ProfileStatus.Incomplete
                        : ProfileStatus.Unknown,
                ),
            ),
        );
    }

    private isAccountNotFound(error: unknown): boolean {
        if (
            !(error instanceof HttpErrorResponse) ||
            error.status !== HttpStatusCode.NotFound
        ) {
            return false;
        }
        const body: ErrorResponse | null = error.error;
        return body?.errorCode === UsersErrorCodes.ACCOUNT_PREFERENCE_NOT_FOUND;
    }

    private exchangeAppSession(): Observable<JWTTokens> {
        this.appSessionExchange$ ??= defer(() =>
            this.tokenStorageService.getJWTToken(),
        ).pipe(
            tap((tokens: JWTTokens) =>
                this.tokenStorageService.storeJWTToken(tokens),
            ),
            finalize(() => (this.appSessionExchange$ = null)),
            shareReplay(1),
        );
        return this.appSessionExchange$;
    }

    private currentUserProfileStatus(): Observable<ProfileStatus> {
        return this.loadCurrentUser().pipe(
            map((response: GetCurrentUser200Response) =>
                response.data.user?.accountMappingsId
                    ? ProfileStatus.Complete
                    : ProfileStatus.Unknown,
            ),
            catchError(() => of(ProfileStatus.Unknown)),
        );
    }

    private loadCurrentUser(): Observable<GetCurrentUser200Response> {
        return this.userService.getCurrentUser().pipe(
            tap((response: any) => {
                if (response.data) {
                    const telephone: AccountTelephone | undefined =
                        response.data.telephone;
                    this.store.dispatch(
                        new UserStateChangeAction({
                            data: {
                                username: response.data.user?.username ?? '',
                                email: response.data.user?.email?.email ?? '',
                                isEmailVerified:
                                    response.data.user?.isEmailVerified ?? '',
                                pictureUrl:
                                    this.getCurrentUserConfig().picture ?? '',
                                ssoId: this.getCurrentUserConfig().sub ?? '',
                                firstname:
                                    response.data.user?.firstname ?? null,
                                lastname: response.data.user?.lastname ?? null,
                                accountMappingsId:
                                    response.data.user?.accountMappingsId ??
                                    null,
                                accountTelephone: telephone ?? null,
                                createdOn:
                                    response.data.user?.createdOn ?? null,
                                registrationStatus:
                                    response.data.user?.status ?? null,
                                spendingCap:
                                    response.data.accountPreference
                                        ?.spendingCap ?? null,
                                isTelephoneVerified:
                                    response.data.user?.isTelephoneVerified,
                                roles:
                                    response.data.accountPreference?.preferences
                                        ?.roles ?? [],
                            },
                        }),
                    );
                }
            }),
        );
    }

    public refreshUserStore(): Observable<any> {
        return this.loadCurrentUser().pipe(
            catchError(() => {
                // Mid-registration: an Auth0 session exists but the app JWT
                // doesn't yet (no account mapping created). The interceptor
                // short-circuits this call for exactly that reason (see
                // TokenInterceptor.preventAnonymousApiCall) rather than
                // letting it round-trip to a 403 — it's an expected state,
                // not a sign the user logged out, so don't wipe the
                // Auth0-populated user info out of the store (that's what
                // was making the header's account menu — Watchlist,
                // Notifications, My Profile — collapse to "Sign in or
                // register" the moment an incomplete-profile user navigated
                // anywhere guarded).
                if (!this.tokenStorageService.getToken()) {
                    return of({ data: null });
                }
                this.store.dispatch(new UserStateChangeAction({ data: null }));
                return of({ data: null });
            }),
        );
    }

    public updateUserStore(): void {
        this.refreshUserStore().subscribe();
    }

    /**
     * Subscribe to user added and update the application state accordingly.
     */
    public subscribeToUserAdded(): void {
        this.authService.user$.subscribe((user: User | null | undefined) => {
            if (user) {
                this.store.dispatch(
                    new UserStateChangeAction({
                        data: {
                            username: '',
                            email: user.email ?? '',
                            isEmailVerified: user.email_verified ?? false,
                            pictureUrl: user.picture ?? '',
                            ssoId: user.sub ?? '',
                            firstname: null,
                            lastname: null,
                            accountMappingsId: null,
                            accountTelephone: null,
                            createdOn: null,
                            registrationStatus: null,
                            spendingCap: null,
                        },
                    }),
                );
            } else {
                this.store.dispatch(new UserStateChangeAction({ data: null }));
            }
        });
    }

    /**
     * Subscribe to user changes and update the application state accordingly.
     */
    public subscribeToUserChanges(): void {
        this.authService.user$.subscribe((user: User | null | undefined) => {
            if (user) {
                this.handleUserChange(user);
            } else {
                this.store.dispatch(new UserStateChangeAction({ data: null }));
            }
        });
    }

    /**
     * Handle user data changes and dispatch appropriate actions.
     */
    private handleUserChange(user: User): void {
        const storedUser: any = this.tokenStorageService.getSessionStorageItem(
            TokenType.USER,
        );
        if (isCompleteStoredUser(storedUser)) {
            try {
                this.store.dispatch(
                    new UserStateChangeAction({ data: storedUser }),
                );
            } catch (error) {
                this.ssoLogin(user);
            }
        } else {
            this.ssoLogin(user);
        }
    }

    private ssoLogin(user: User): void {
        if (this.tokenStorageService.isMasquerading()) {
            return;
        }

        this.exchangeAppSession()
            .pipe(
                switchMap(() => this.userService.getCurrentUser()),
                catchError(() => {
                    this.dispatchUserStateChange(user);
                    return EMPTY;
                }),
            )
            .subscribe((response: any) => {
                if (response.data) {
                    const telephone: AccountTelephone | undefined =
                        response.data.telephone;
                    this.dispatchUserStateChange(
                        user,
                        response.data.user,
                        telephone,
                        response.data.user.createdOn,
                        response.data.user.status,
                        response.data.accountPreference.spendingCap,
                        response.data.accountPreference.preferences?.roles,
                    );
                }
            });
        const auth0Token: string | null =
            this.tokenStorageService.getAuth0Token();
        if (!auth0Token) {
            return;
        }
    }

    /**
     * Dispatch user state change action with or without account mapping details.
     */
    private dispatchUserStateChange(
        user: User,
        clientuser?: ClientUser,
        clientTelephone?: AccountTelephone,
        createdOn?: string,
        registrationStatus?: string,
        spendingCap?: number,
        roles?: string[],
    ): void {
        const userData: any = {
            username: clientuser?.username ?? '',
            email: user.email ?? '',
            isEmailVerified: user.email_verified ?? false,
            pictureUrl: user.picture ?? '',
            ssoId: user.sub ?? '',
            firstname: clientuser?.firstname ?? null,
            lastname: clientuser?.lastname ?? null,
            accountMappingsId: clientuser?.accountMappingsId ?? null,
            accountTelephone: clientTelephone ?? null,
            createdOn: createdOn ?? null,
            registrationStatus: registrationStatus ?? null,
            spendingCap: spendingCap ?? null,
            isTelephoneVerified: clientTelephone?.isVerified,
            roles: roles ?? [],
        };
        this.store.dispatch(
            new UserStateChangeAction({
                data: userData,
            }),
        );
        this.tokenStorageService.setSessionStorageItem(
            TokenType.USER,
            userData,
        );
    }
}
